// Media Service
// Handles media upload, validation, and processing

const MAX_FILE_SIZE = {
  photo: 10 * 1024 * 1024, // 10MB
  video: 100 * 1024 * 1024, // 100MB
  document: 25 * 1024 * 1024 // 25MB
};

const ALLOWED_FILE_TYPES = {
  photo: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
  video: ['video/mp4', 'video/mpeg', 'video/quicktime', 'video/x-msvideo', 'video/webm'],
  document: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/jpeg',
    'image/jpg',
    'image/png'
  ]
};

const VIDEO_PLATFORMS = {
  youtube: {
    regex: /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/,
    getEmbedUrl: (id) => `https://www.youtube.com/embed/${id}`
  },
  vimeo: {
    regex: /vimeo\.com\/(\d+)/,
    getEmbedUrl: (id) => `https://player.vimeo.com/video/${id}`
  },
  instagram: {
    regex: /instagram\.com\/p\/([a-zA-Z0-9_-]+)/,
    getEmbedUrl: (id) => `https://www.instagram.com/p/${id}/embed`
  },
  tiktok: {
    regex: /tiktok\.com\/@[\w.-]+\/video\/(\d+)/,
    getEmbedUrl: (id) => `https://www.tiktok.com/embed/${id}`
  }
};

class MediaService {
  /**
   * Validate file before upload
   */
  validateFile(file, type) {
    // Check file size
    if (file.size > MAX_FILE_SIZE[type]) {
      const maxSizeMB = MAX_FILE_SIZE[type] / (1024 * 1024);
      throw new Error(`File size exceeds ${maxSizeMB}MB limit`);
    }

    // Check file type
    if (!ALLOWED_FILE_TYPES[type].includes(file.type)) {
      throw new Error(`Invalid file type. Allowed types: ${ALLOWED_FILE_TYPES[type].join(', ')}`);
    }

    return true;
  }

  /**
   * Prepare form data for media upload
   */
  prepareMediaUpload(file, metadata) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', metadata.type);
    
    if (metadata.title) formData.append('title', metadata.title);
    if (metadata.description) formData.append('description', metadata.description);
    if (metadata.tags) formData.append('tags', JSON.stringify(metadata.tags));
    if (metadata.sport) formData.append('sport', metadata.sport);
    if (metadata.date) formData.append('date', metadata.date);
    if (metadata.featured !== undefined) formData.append('featured', metadata.featured);

    return formData;
  }

  /**
   * Parse video URL and extract platform info
   */
  parseVideoUrl(url) {
    for (const [platform, config] of Object.entries(VIDEO_PLATFORMS)) {
      const match = url.match(config.regex);
      if (match) {
        return {
          platform,
          videoId: match[1],
          embedUrl: config.getEmbedUrl(match[1]),
          originalUrl: url
        };
      }
    }

    return {
      platform: 'other',
      videoId: null,
      embedUrl: url,
      originalUrl: url
    };
  }

  /**
   * Generate thumbnail from video file
   */
  async generateVideoThumbnail(videoFile) {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      video.addEventListener('loadeddata', () => {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        
        // Capture frame at 1 second
        video.currentTime = 1;
      });

      video.addEventListener('seeked', () => {
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          resolve(blob);
        }, 'image/jpeg', 0.8);
      });

      video.src = URL.createObjectURL(videoFile);
      video.load();
    });
  }

  /**
   * Compress image if needed
   */
  async compressImage(file, maxWidth = 1920, quality = 0.9) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const img = new Image();
        
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          let width = img.width;
          let height = img.height;
          
          // Calculate new dimensions
          if (width > maxWidth) {
            height = (maxWidth / width) * height;
            width = maxWidth;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Draw and compress
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob((blob) => {
            resolve(new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now()
            }));
          }, 'image/jpeg', quality);
        };
        
        img.src = e.target.result;
      };
      
      reader.readAsDataURL(file);
    });
  }

  /**
   * Get file preview URL
   */
  getFilePreview(file) {
    if (file instanceof File) {
      return URL.createObjectURL(file);
    }
    return file.url || file;
  }

  /**
   * Clean up preview URLs to prevent memory leaks
   */
  revokePreviewUrl(url) {
    if (url && url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Get file icon based on type
   */
  getFileIcon(fileType) {
    if (fileType.startsWith('image/')) return 'FaImage';
    if (fileType.startsWith('video/')) return 'FaVideo';
    if (fileType === 'application/pdf') return 'FaFilePdf';
    return 'FaFile';
  }

  /**
   * Sort media items
   */
  sortMedia(mediaItems, sortBy = 'date') {
    const sorted = [...mediaItems];
    
    switch (sortBy) {
      case 'date':
        return sorted.sort((a, b) => new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt));
      
      case 'title':
        return sorted.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
      
      case 'type':
        return sorted.sort((a, b) => a.type.localeCompare(b.type));
      
      case 'featured':
        return sorted.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
      
      default:
        return sorted;
    }
  }

  /**
   * Filter media items
   */
  filterMedia(mediaItems, filters = {}) {
    let filtered = [...mediaItems];
    
    if (filters.type) {
      filtered = filtered.filter(item => item.type === filters.type);
    }
    
    if (filters.sport) {
      filtered = filtered.filter(item => item.sport === filters.sport);
    }
    
    if (filters.featured !== undefined) {
      filtered = filtered.filter(item => item.featured === filters.featured);
    }
    
    if (filters.tags?.length) {
      filtered = filtered.filter(item =>
        filters.tags.some(tag => item.tags?.includes(tag))
      );
    }
    
    if (filters.dateFrom) {
      filtered = filtered.filter(item =>
        new Date(item.date || item.createdAt) >= new Date(filters.dateFrom)
      );
    }
    
    if (filters.dateTo) {
      filtered = filtered.filter(item =>
        new Date(item.date || item.createdAt) <= new Date(filters.dateTo)
      );
    }
    
    return filtered;
  }
}

export default new MediaService();
